import { useCallback, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  commitTask,
  commitTasks,
  uncommitTask,
  TaskCommitRejectedError,
  type ICommitRejection,
} from '@apis/schedule';
import { apiErrorCode } from '@d-orca/shared/client/apis';
import { committedActivitiesKey, WEATHER_DAY_KEY } from '@constants/queryKeys';
import type { SiteId, TaskId } from '@interfaces/IScheduler';
import { useTranslation } from 'react-i18next';
import { useToast } from '@components/ui/Toasts/toastContext';
import { useSchedulerActions, useSchedulerStale } from './useSchedulerStore';
import { usePullPlan } from './usePullPlan';
import { useRestoreServerState } from './useRestoreServerState';
import type { ICommitForm } from '../lib/commitDraft';

// ---------------------------------------------------------------------------
// useTaskCommitActions — committing a task and taking it back.
//
// A commit is not an immediate-write like the slider: it creates records in
// Trusstor, it can be REFUSED with a list of offending rows, and a parent
// commit fans out over every leaf beneath it. So it is an awaited call with a
// visible outcome, not a queued one — and the schedule is re-read afterwards so
// the whole subtree picks up what the server wrote (a parent commit changes N
// rows, which is more than an optimistic patch should try to guess).
//
// PLAN FILLS: the review's fills (profession, areas, kind) are written to the
// TASK server-side, so the re-read afterwards is `usePullPlan`'s three-way
// rebase rather than a bare invalidate (which would leave the store holding the
// pre-commit task, and the next Save writing it back over the fill).
//
// STALE GUARD: like every other write, a schedule behind upstream is frozen —
// committing on a stale base would promise work against a plan that has moved.
//
// FEEDBACK goes to the app's shared toast queue (`useToast`), so the drawer
// reports the same way wherever it is hosted. A failed commit also comes back
// in the outcome as `error`, for the review to keep on screen after the toast
// is dismissed.
// ---------------------------------------------------------------------------

/**
 * Why a take-back was refused, by the server's code — each names its own way
 * out. Anything else (an outage, a refusal with no code) is a plain failure.
 */
const UNCOMMIT_REFUSALS: Readonly<Record<string, string>> = {
  // Work time is already recorded against the activity: the exits are done
  // or deleting the task.
  ACTIVITY_IN_USE: 'scheduler.commit.uncommitRefused',
  // Someone else took it back first: this page was behind.
  TASK_NOT_COMMITTED: 'scheduler.commit.alreadyUncommitted',
};

export type { ICommitForm } from '../lib/commitDraft';

/** One row of a list-form commit: a leaf id and its own form. */
export type ICommitItem = ICommitForm & { taskId: TaskId };

/**
 * How a commit ended. THREE outcomes, not two — a refusal and a failure are
 * different answers and the review must treat them differently:
 *   · `ok`                      the promise was made; the review may close.
 *   · `!ok` with `rejections`   the server refused and named the rows to fix;
 *                               the review stays open holding that list.
 *   · `!ok` with `error`        the call itself failed (Trusstor unreachable,
 *                               network). A toast said so and `error` holds
 *                               the same words; the review STAYS OPEN, showing
 *                               them, so the answers the user just typed are
 *                               not thrown away on a retryable error.
 *   · `!ok` with neither        nothing was attempted (stale base, not ready).
 */
export interface ICommitOutcome {
  ok: boolean;
  rejections: ICommitRejection[];
  error?: string;
}

export interface ITaskCommitActions {
  /** Commit the task (or, for a parent, every leaf under it). */
  commit: (form: ICommitForm) => Promise<ICommitOutcome>;
  /** Commit a chosen SET of leaves, all-or-nothing (the list-form review). */
  commitMany: (items: readonly ICommitItem[]) => Promise<ICommitOutcome>;
  /** Give the promise back. Refused once the activity has recorded work time. */
  uncommit: () => Promise<void>;
  pending: boolean;
}

export interface IUseTaskCommitActionsArgs {
  siteId: SiteId;
  taskId: TaskId;
  /** Loaded schedule number — absent disables the calls (Router-free tests). */
  scheduleNumber?: string;
}

export function useTaskCommitActions({
  siteId,
  taskId,
  scheduleNumber,
}: IUseTaskCommitActionsArgs): ITaskCommitActions {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const pushToast = useToast();
  const restore = useRestoreServerState(siteId);
  const stale = useSchedulerStale(siteId);
  const { taskCommitted, taskUncommitted } = useSchedulerActions();
  const [pending, setPending] = useState(false);
  // The server's post-commit plan, rebased onto the store (see PLAN FILLS above).
  const pullPlan = usePullPlan(siteId);

  const refresh = useCallback(() => {
    if (scheduleNumber) void pullPlan(scheduleNumber);
    // The Activities page is indexed from the overlays a commit creates — a new
    // promise adds a row there, taking one back removes it. The weather card
    // weighs the forecast on those same promises.
    void qc.invalidateQueries({ queryKey: committedActivitiesKey(siteId) });
    void qc.invalidateQueries({ queryKey: WEATHER_DAY_KEY });
  }, [qc, siteId, scheduleNumber, pullPlan]);

  // Both commit paths share one outcome: mirror each committed row locally so
  // the chips flip before the refetch lands (the invalidation is what makes it
  // authoritative), then say so. A refusal is an ANSWER, not a failure: the
  // caller lists the offending rows so they can be fixed. Anything else is a
  // failure. The form's PLAN fills are not mirrored — the rebase brings them.
  const run = useCallback(
    async (
      request: () => Promise<{ taskId: TaskId; trusstorGroupId: string | null }[]>,
      formFor: (id: TaskId) => ICommitForm,
    ): Promise<ICommitOutcome> => {
      // Frozen (stale base) or not ready: nothing was attempted, so nothing is
      // ok — the review stays as it is rather than closing on a no-op.
      if (stale || !scheduleNumber || pending) return { ok: false, rejections: [] };
      setPending(true);
      try {
        const committed = await request();
        for (const row of committed) {
          const form = formFor(row.taskId);
          taskCommitted({
            siteId,
            taskId: row.taskId,
            dangerousActivity: form.dangerousActivity,
            assignee: form.assignee,
            trusstorGroupId: row.trusstorGroupId,
          });
        }
        refresh();
        pushToast({ kind: 'success', title: t('scheduler.commit.committed', { count: committed.length }) });
        return { ok: true, rejections: [] };
      } catch (error) {
        if (error instanceof TaskCommitRejectedError) return { ok: false, rejections: error.rejections };
        const failure = { title: t('scheduler.commit.failed'), sub: t('scheduler.commit.failedSub') };
        pushToast({ kind: 'error', ...failure });
        return { ok: false, rejections: [], error: `${failure.title} — ${failure.sub}` };
      } finally {
        setPending(false);
      }
    },
    [stale, scheduleNumber, pending, siteId, taskCommitted, refresh, pushToast, t],
  );

  const commit = useCallback(
    (form: ICommitForm) => run(() => commitTask(siteId, scheduleNumber ?? '', taskId, form), () => form),
    [run, siteId, scheduleNumber, taskId],
  );

  const commitMany = useCallback(
    (items: readonly ICommitItem[]) => {
      const byId = new Map(items.map((item) => [item.taskId, item] as const));
      return run(
        () => commitTasks(siteId, scheduleNumber ?? '', items),
        (id) => byId.get(id) ?? { dangerousActivity: false, assignee: null },
      );
    },
    [run, siteId, scheduleNumber],
  );

  const uncommit = useCallback(async () => {
    if (stale || !scheduleNumber || pending) return;
    setPending(true);
    try {
      await uncommitTask(siteId, scheduleNumber, taskId);
      taskUncommitted({ siteId, taskId });
      refresh();
      pushToast({ kind: 'success', title: t('scheduler.commit.uncommitted') });
    } catch (error) {
      const code = apiErrorCode(error);
      // Behind the server: show what it holds — the task is not committed.
      if (code === 'TASK_NOT_COMMITTED') await restore(scheduleNumber);
      const refusal = UNCOMMIT_REFUSALS[code ?? ''];
      pushToast({ kind: 'error', title: t(refusal ?? 'scheduler.commit.uncommitFailed') });
    } finally {
      setPending(false);
    }
  }, [stale, scheduleNumber, pending, siteId, taskId, taskUncommitted, refresh, restore, pushToast, t]);

  return { commit, commitMany, uncommit, pending };
}
